import filterResponse from "../filterResponse";
import sha256Stream from "./sha256Stream";
import createSemaphore from "./semaphore";
import runtimeOverride from "./runtimeOverride";
import chooseUploadTarget, { betterTargetFor, startPart } from "./fastestUploadTarget";
import { cancelledError, throwIfCancelled } from "./cancelled";

// Uploads a file straight to DigitalOcean Spaces using presigned multipart URLs.
// The file never passes through the backend container, so size is not bounded by its RAM.
//
// Resumable across page reloads. The object key is the file's SHA-256, so the same bytes always
// address the same upload: POST /files/initiate looks up the multipart upload already in flight
// for that key and returns the parts that landed. Nothing about the resume depends on browser
// state, so a refresh, a crash, a closed laptop or a flat battery costs the re-hash and not the
// gigabytes already transferred. (The hash itself is cached below, so usually not even that.)

const ENDPOINT = process.env.REACT_APP_ENDPOINT;

// Parts multiplex over a single HTTP/2 connection to Spaces, so throughput is bounded by that
// connection's flow-control window rather than by this number — raising it does not make the
// upload faster. It is above one so that a stalled or retrying part cannot idle the wire.
//
// Measured BR -> nyc3 with independent TCP connections, uploads plateaued at 5-9 MB/s and got
// *worse* at concurrency 4 (~5 MB/s), while the same path downloaded at 41 MB/s. That says the
// ceiling is on the Spaces side, not ours. Those measurements used separate connections though,
// which is not what the browser does, so the right value here is an open question — hence the
// override below, so it can be A/B tested against a real deploy without a rebuild.
//
// DigitalOcean have since ruled out their own rate limiting on both buckets (ticket #12805625).
// What they do see is a handful of individual requests whose time-to-first-byte spikes to over a
// minute, sometimes several, while the requests around them answer normally — intermittent
// backend or network delay rather than a limit. So a low number here is not buying us headroom
// against a throttle; it is only about how thinly the uplink is divided.
const DEFAULT_MAX_CONCURRENT_PARTS = 6;

function maxConcurrentParts() {
    return runtimeOverride({
        queryKey: "uploadConcurrency",
        storageKey: "crowd.uploadConcurrency",
        fallback: DEFAULT_MAX_CONCURRENT_PARTS,
        max: 16,
    });
}

// The budget is for the page, not for one upload. Several files upload at a time now (see
// uploadQueue), and a per-upload budget would multiply: two files would put twice the parts on a
// connection that was already the bottleneck, which splits the same uplink more ways rather than
// widening it. Whatever is in flight, in total, is this many parts — the same pressure the
// single-file case has always applied, and the case the measurements above were taken against.
const partSlots = createSemaphore(maxConcurrentParts);

const SIGN_BATCH = 25;             // sign URLs as workers reach them, not all up front
const MAX_PART_ATTEMPTS = 10;
// Spaces answers 503 Slow Down when an account exceeds its operations/second allowance, and
// DigitalOcean's guidance is to back off and retry rather than treat it as an error. Being
// throttled is not the part failing, so it gets its own generous allowance instead of eating
// the retry budget — otherwise a throttled upload dies on a condition it was told to wait out.
const MAX_THROTTLE_RETRIES = 30;
const MAX_BACKOFF_MS = 30 * 1000;
const STALL_TIMEOUT_MS = 60 * 1000; // no bytes moved for this long -> drop it and retry
// Once the last byte of the part is sent there is nothing left to make progress, so the stall
// detector above cannot be applied to the wait for the response — it would abort a part whose
// bytes all landed. DigitalOcean have measured exactly that wait on our buckets: a handful of
// requests whose time-to-first-byte spikes past a minute, sometimes to several, while the requests
// around them answer normally. Aborting one of those costs the whole part over again, so the wait
// gets its own far longer deadline, there only to catch a connection that is genuinely dead.
const RESPONSE_TIMEOUT_MS = 10 * 60 * 1000;
const OFFLINE_WAIT_MS = 5 * 60 * 1000;
const HASH_CACHE_KEY = "crowd.uploadHashes";
const HASH_CACHE_LIMIT = 20;

// A part blob otherwise inherits the file's type (e.g. video/mp4). Sending a plain type keeps
// the request minimal and avoids any chance of a signed-header mismatch; it cannot corrupt what
// is stored, because the part URL does not sign Content-Type and the object's real type comes
// from CreateMultipartUpload server-side.
//
// It does NOT avoid the CORS preflight, despite what an earlier version of this comment claimed:
// only GET, HEAD and POST can ever be "simple" requests, so a cross-origin PUT is preflighted
// whatever its content type. Nor can Access-Control-Max-Age help, since the preflight cache is
// keyed by URL and every part has its own presigned one. So each part costs one extra round trip
// (~74 ms to nyc3) — real, but far too small to explain the throughput ceiling.
const SAFELISTED_SLICE_TYPE = "text/plain";

class UploadGoneError extends Error {}

// This file's bucket is uploading far slower than another one, early enough that restarting there
// is quicker than finishing (see fastestUploadTarget.betterTargetFor).
class MoveUploadError extends Error {
    constructor(storageTargetId) {
        super("A faster bucket is available");
        this.storageTargetId = storageTargetId;
    }
}

// Hashing multiple GB takes minutes, and a contributor who reloads should not pay it twice.
// Keyed on the identity the browser gives us for free; the server still verifies nothing is
// overwritten, so a stale entry can only cost a wasted upload attempt, never a wrong file.
function fileIdentity(file) {
    return `${file.name}|${file.size}|${file.lastModified}`;
}

function readHashCache() {
    try {
        return JSON.parse(localStorage.getItem(HASH_CACHE_KEY)) || {};
    }
    catch (e) {
        console.warn("Could not read the upload hash cache; re-hashing.", e);
        return {};
    }
}

function getCachedHash(file) {
    return readHashCache()[fileIdentity(file)]?.hash || null;
}

function cacheHash(file, hash) {
    try {
        const cache = readHashCache();
        cache[fileIdentity(file)] = { hash, at: Date.now() };

        const entries = Object.entries(cache).sort((a, b) => b[1].at - a[1].at).slice(0, HASH_CACHE_LIMIT);
        localStorage.setItem(HASH_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
    }
    catch (e) {
        // Private browsing, or the quota is full. Losing the cache only costs a re-hash.
        console.warn("Could not cache the upload hash.", e);
    }
}

// In a worker where the browser has them, so the files hashing at once each get a core instead of
// sharing the page's thread. On the page otherwise, or if the worker will not start.
async function sha256Hex(file, onProgress, signal) {
    if (typeof Worker !== "undefined") {
        try {
            const { default: sha256InWorker } = await import("./hashInWorker");
            return await sha256InWorker(file, onProgress, signal);
        }
        catch (e) {
            if (e.code === "CANCELLED") {
                throw e;
            }
            console.warn(`Could not hash ${file.name} in a background thread; hashing it on the page instead.`, e);
        }
    }
    return sha256Stream(file, onProgress, () => !!(signal && signal.aborted));
}

// The object key is the file's hash, so this has to happen before the upload can start — and on a
// multi-GB file it takes minutes of solid disk reading. Exported so the queue can run it for the
// files next in line while the ones ahead of them are on the wire, instead of every upload
// stopping to read its own file first.
export async function hashFile(file, onProgress, signal) {
    const cached = getCachedHash(file);
    if (cached) {
        return cached;
    }

    const hash = await sha256Hex(file, onProgress, signal);
    cacheHash(file, hash);
    return hash;
}

// Every step of an upload goes through here, so this is where the upload learns that the job closed
// under it (archived, or the submission delivered from Inspect). The page is being taken down at
// that point, so stop rather than retry or report the step as failed.
async function api(path, body) {
    const response = await fetch(ENDPOINT + path, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    const closed = await filterResponse(response);
    if (closed?.jobClosed) {
        const err = new Error(closed.error);
        err.code = "JOB_CLOSED";
        throw err;
    }

    return response;
}

function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        if (signal) {
            signal.addEventListener("abort", () => {
                clearTimeout(timer);
                reject(new Error("Upload aborted"));
            }, { once: true });
        }
    });
}

// While the connection is down there is nothing to retry into, and burning the attempt budget
// against a dead network is how a contributor loses an upload they were minutes from finishing.
// Wait for the browser to say it is back instead, up to a bound.
function waitForOnline(signal) {
    if (navigator.onLine !== false) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const finish = () => {
            window.removeEventListener("online", finish);
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(finish, OFFLINE_WAIT_MS);
        window.addEventListener("online", finish, { once: true });

        if (signal) {
            signal.addEventListener("abort", () => {
                window.removeEventListener("online", finish);
                clearTimeout(timer);
                reject(new Error("Upload aborted"));
            }, { once: true });
        }
    });
}

// PUT a single part directly to Spaces. The backend reads the part ETags from Spaces at
// completion time, so the browser doesn't need to read the ETag response header (which would
// require a CORS ExposeHeaders rule).
//
// There is no overall deadline, because a 64 MB part on a slow connection is legitimately slow.
// What is not legitimate is a connection that stops moving bytes, so that is what is detected.
function putPart(url, blob, { signal, onProgress, stallTimeoutMs = STALL_TIMEOUT_MS, responseTimeoutMs = RESPONSE_TIMEOUT_MS } = {}) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let lastProgressAt = Date.now();
        let bodySentAt = null;
        let settled = false;

        // A fraction of the shorter deadline, so a check lands well before it expires. At the real
        // timeouts this is the 5 s it has always been; the tuning seam shortens both together.
        const checkEvery = Math.min(5000, Math.max(50, Math.floor(Math.min(stallTimeoutMs, responseTimeoutMs) / 4)));
        const stallCheck = setInterval(() => {
            if (bodySentAt !== null) {
                if (Date.now() - bodySentAt > responseTimeoutMs) {
                    settle(() => reject(new Error("Spaces did not answer the part upload")), true);
                }
                return;
            }
            if (Date.now() - lastProgressAt > stallTimeoutMs) {
                settle(() => reject(new Error("Part upload stalled")), true);
            }
        }, checkEvery);

        function settle(action, abortRequest) {
            if (settled) {
                return;
            }
            settled = true;
            clearInterval(stallCheck);
            if (abortRequest) {
                xhr.abort();
            }
            action();
        }

        xhr.open("PUT", url);

        xhr.upload.onprogress = (e) => {
            lastProgressAt = Date.now();
            onProgress && onProgress(e.loaded);
        };
        // The body is fully transmitted; from here the request is waiting on Spaces, not moving.
        xhr.upload.onload = () => {
            bodySentAt = Date.now();
        };
        xhr.onload = () => settle(() => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            }
            else {
                const error = new Error("Part upload failed with status " + xhr.status);
                error.status = xhr.status;
                reject(error);
            }
        });
        xhr.onerror = () => settle(() => reject(new Error("Network error during part upload")));
        xhr.ontimeout = () => settle(() => reject(new Error("Part upload timed out")));

        if (signal) {
            if (signal.aborted) {
                return settle(() => reject(new Error("Upload aborted")), true);
            }
            signal.addEventListener("abort", () => settle(() => reject(new Error("Upload aborted")), true), { once: true });
        }

        xhr.send(blob);
    });
}

async function startUpload(file, crowdJobId, hash, fileInputId, storageTargetId) {
    const initRes = await api("/files/initiate", {
        crowdJobId,
        storageTargetId,
        fileInputId,
        hash,
        contentType: file.type,
        fileName: file.name,
        size: file.size,
    });

    if (initRes.status === 409) {
        // Two different refusals share this status: the file is already in this job, or it was
        // collected in another one (DEV-3046). The second needs the server's own wording, because
        // "already uploaded" sends a contributor looking for it in a job where it isn't.
        const { error } = await initRes.json().catch(() => ({}));
        const err = new Error(error || "This file has already been uploaded!");
        err.code = "DUPLICATE";
        throw err;
    }
    if (initRes.status === 413) {
        // The server states the job's actual limit; keep its wording so the contributor is told
        // the number rather than just "too large".
        const { error } = await initRes.json().catch(() => ({}));
        const err = new Error(error || "This file is too large to upload.");
        err.code = "TOO_LARGE";
        throw err;
    }
    if (!initRes.ok) {
        throw new Error("Failed to initiate upload");
    }
    return initRes.json();
}

// `tuning` is a test seam: it lets the retry-exhaustion path be exercised without sitting
// through minutes of real backoff. Callers in the app leave it alone.
export default async function uploadToSpaces(file, crowdJobId, { onProgress, signal, fileInputId, hash: precomputedHash, tuning } = {}) {
    const {
        maxPartAttempts = MAX_PART_ATTEMPTS,
        maxBackoffMs = MAX_BACKOFF_MS,
        concurrency = maxConcurrentParts(),
        stallTimeoutMs = STALL_TIMEOUT_MS,
        responseTimeoutMs = RESPONSE_TIMEOUT_MS,
    } = tuning || {};
    const report = (loaded, total, phase) => onProgress && onProgress(loaded, total, phase);

    // Normally the queue has already hashed this file while another was uploading. Hashing here
    // when it has not keeps this function usable on its own.
    const hash = precomputedHash
        || await hashFile(file, (loaded, total) => report(loaded, total, "hashing"), signal);

    // Step 1: dedup, resume an upload already in flight for these bytes, or start a new one — in
    // the bucket uploading fastest for this contributor, when the job offers more than one.
    const storageTargetId = await chooseUploadTarget(crowdJobId);
    throwIfCancelled(signal);
    let session = await startUpload(file, crowdJobId, hash, fileInputId, storageTargetId);

    if (session.alreadyUploaded) {
        report(file.size, file.size, "uploading");
        return session.key;
    }

    const { key, partSize } = session;
    const partCount = Math.max(1, Math.ceil(file.size / partSize));
    const partNumbers = Array.from({ length: partCount }, (_, i) => i + 1);

    let uploadId = session.uploadId;
    let done = new Set((session.uploadedParts || []).map((p) => p.PartNumber));
    let uploadedBytes = (session.uploadedParts || []).reduce((sum, p) => sum + (p.Size || 0), 0);
    report(uploadedBytes, file.size, session.resumed ? "resuming" : "uploading");

    // Baseline for the throughput readout below: on a resume only the remaining bytes are ours.
    const startingBytes = uploadedBytes;
    const transferStartedAt = Date.now();
    let throttled = 0; // count of 503 Slow Down responses from Spaces

    let missing = partNumbers.filter((n) => !done.has(n));
    const urls = {};
    let signInFlight = null;
    // Bumped when the upload session is replaced. URLs signed against the old uploadId are
    // worthless, and a request that was already in flight must not write them back over the new
    // ones once it lands.
    let generation = 0;

    async function signBatch(batch) {
        const signedFor = generation;
        // crowdJobId travels with every step because the backend resolves the job's storage
        // destination itself rather than letting the client name any bucket. Without it the parts
        // would be signed for the default destination while the upload lives in the job's. The
        // nearby destination initiate settled on travels too, and the backend only accepts one it
        // offers for this job.
        const signRes = await api("/files/sign-parts", { key, uploadId, partNumbers: batch, crowdJobId, storageTargetId: session.storageTargetId });
        if (!signRes.ok) {
            throw new Error("Failed to sign upload parts");
        }
        const { urls: signed } = await signRes.json();
        if (signedFor === generation) {
            Object.assign(urls, signed);
        }
    }

    // One signing request at a time, shared by every worker: they all want the next batch, and
    // asking six times over would just be six times the round trips.
    function signNextBatch(partNumber) {
        const batch = missing.filter((n) => !urls[n]).slice(0, SIGN_BATCH);
        if (!batch.includes(partNumber)) {
            batch.push(partNumber);
        }
        // Clear only if still the current request: a restart may have replaced it already.
        const pending = signBatch(batch).finally(() => {
            if (signInFlight === pending) {
                signInFlight = null;
            }
        });
        signInFlight = pending;
        return pending;
    }

    async function urlFor(partNumber) {
        while (!urls[partNumber]) {
            await (signInFlight || signNextBatch(partNumber));
        }
        return urls[partNumber];
    }

    async function uploadPart(partNumber, phaseSignal) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const blob = file.slice(start, end, SAFELISTED_SLICE_TYPE);
        let throttleRetries = 0;

        let lastLoaded = 0;
        const onPartProgress = (loaded) => {
            uploadedBytes += loaded - lastLoaded;
            lastLoaded = loaded;
            report(uploadedBytes, file.size, "uploading");
        };

        for (let attempt = 1; ; attempt++) {
            try {
                // Signed before taking a slot, and the slot given back before the backoff below:
                // a part budget is a budget of bytes on the wire, and holding one through a round
                // trip to our own backend or through thirty seconds of waiting would keep another
                // file's part off the connection for no gain.
                const url = await urlFor(partNumber);
                const releasePartSlot = await partSlots.acquire();
                try {
                    const partLanded = startPart(session.storageTargetId);
                    try {
                        await putPart(url, blob, {
                            signal: phaseSignal, onProgress: onPartProgress, stallTimeoutMs, responseTimeoutMs,
                        });
                        partLanded(blob.size);
                    }
                    catch (e) {
                        partLanded(0);
                        throw e;
                    }
                }
                finally {
                    releasePartSlot();
                }
                return;
            }
            catch (e) {
                // roll back this part's partial progress before retrying
                uploadedBytes -= lastLoaded;
                lastLoaded = 0;
                report(uploadedBytes, file.size, "uploading");

                if (phaseSignal.aborted) {
                    throw e;
                }
                // The job closed under us. Retrying spends ten attempts and minutes of backoff
                // asking a question that has been answered, so it is as final as an abort.
                if (e.code === "JOB_CLOSED") {
                    throw e;
                }
                // 404 means the multipart upload itself is gone (completed elsewhere, aborted,
                // or expired). Retrying the part is pointless; the whole session has to restart.
                if (e.status === 404) {
                    throw new UploadGoneError("The upload session expired");
                }
                // 403 is an expired signature, which is expected on an upload long enough to
                // outlive one. Re-sign rather than spend the attempt budget on it.
                if (e.status === 403) {
                    delete urls[partNumber];
                }
                // Being throttled is Spaces telling us to wait, not a failure of this part.
                // Counted so the summary below can say whether throttling shaped the upload —
                // this is the evidence needed to tell "Spaces is rate-limiting us" apart from
                // "the network is slow", which look identical from a progress bar.
                if (e.status === 503) {
                    throttled++;
                    if (++throttleRetries <= MAX_THROTTLE_RETRIES) {
                        attempt--;
                    }
                }
                if (attempt >= maxPartAttempts) {
                    throw e;
                }

                await waitForOnline(phaseSignal);
                const backoff = Math.min(maxBackoffMs, 1000 * 2 ** (attempt - 1));
                await sleep(backoff / 2 + Math.random() * backoff / 2, phaseSignal); // jitter, so retries don't sync up
            }
        }
    }

    // Runs the workers over `missing` and does not return until every one of them has stopped.
    // That last part matters: when one worker gives up, the caller may replace the whole upload
    // session, and a sibling still PUTting against the dead uploadId would corrupt the byte
    // accounting and re-populate the URL cache behind its back. So the first failure cancels the
    // rest, and the original error — not whichever cancellation lands first — is what surfaces.
    async function uploadAllParts() {
        const phase = new AbortController();
        const cancelPhase = () => phase.abort();
        if (signal) {
            if (signal.aborted) {
                phase.abort();
            }
            signal.addEventListener("abort", cancelPhase, { once: true });
        }

        let cursor = 0;
        let firstError = null;
        const worker = async () => {
            while (cursor < missing.length && !phase.signal.aborted) {
                try {
                    await uploadPart(missing[cursor++], phase.signal);
                    if (!moved) {
                        const faster = await betterTargetFor(crowdJobId, session.storageTargetId, uploadedBytes / file.size);
                        if (faster && !phase.signal.aborted) {
                            throw new MoveUploadError(faster);
                        }
                    }
                }
                catch (e) {
                    firstError = firstError || e;
                    phase.abort();
                    throw e;
                }
            }
        };

        try {
            await Promise.allSettled(Array.from(
                { length: Math.min(concurrency, missing.length || 1) },
                worker,
            ));
            if (firstError) {
                throw firstError;
            }
        }
        finally {
            if (signal) {
                signal.removeEventListener("abort", cancelPhase);
            }
        }
    }

    // Cancelled: what was sent is thrown away rather than left for a resume, because the contributor
    // has said this is not the file they meant.
    async function discardCancelled() {
        const abortRes = await api("/files/abort", { key, uploadId, crowdJobId, storageTargetId: session.storageTargetId })
            .catch(abortError => abortError);
        if (!abortRes?.ok) {
            console.warn("Could not discard the cancelled upload; its parts stay until the bucket clears them.", abortRes);
        }
        throw cancelledError();
    }

    // An expired session restarts once, and a file moves to a faster bucket at most once, so a file
    // cannot bounce between two buckets whose speeds keep swapping.
    let restarted = false;
    let moved = false;
    for (;;) {
        try {
            await uploadAllParts();
            break;
        }
        catch (e) {
            if (signal && signal.aborted) {
                await discardCancelled();
            }
            let nextTargetId = session.storageTargetId;
            if (e instanceof MoveUploadError && !moved) {
                moved = true;
                nextTargetId = e.storageTargetId;
                // Discarded first, or initiate would find these parts and resume here, in the slow bucket.
                const abortRes = await api("/files/abort", { key, uploadId, crowdJobId, storageTargetId: session.storageTargetId })
                    .catch(abortError => abortError);
                if (!abortRes?.ok) {
                    console.warn("Could not discard the upload being moved; it will be resumed where it is.", abortRes);
                }
            }
            else if (e instanceof UploadGoneError && !restarted) {
                restarted = true;
                // Start again against a fresh session. Anything already in Spaces is still there, so
                // this normally re-uploads only what was outstanding — and if another client finished
                // the identical file meanwhile, initiate says so and we are done.
                console.warn("Upload session expired; re-initiating.", e);
            }
            else {
                throw e;
            }

            session = await startUpload(file, crowdJobId, hash, fileInputId, nextTargetId);
            if (session.alreadyUploaded) {
                report(file.size, file.size, "uploading");
                return session.key;
            }

            generation++; // discards any signing request still in flight for the dead session
            signInFlight = null;
            uploadId = session.uploadId;
            const landed = new Set((session.uploadedParts || []).map((p) => p.PartNumber));
            done = landed;
            uploadedBytes = (session.uploadedParts || []).reduce((sum, p) => sum + (p.Size || 0), 0);
            missing = partNumbers.filter((n) => !landed.has(n));
            for (const partNumber of Object.keys(urls)) {
                delete urls[partNumber]; // signed against the dead uploadId
            }
            report(uploadedBytes, file.size, "resuming");
        }
    }

    // Every part may have landed in the moment Cancel was pressed; the file must still not be kept.
    if (signal && signal.aborted) {
        await discardCancelled();
    }

    // Wall-clock throughput for the transfer that actually happened. The progress bar cannot be
    // used to compare settings: xhr.upload.onprogress counts bytes queued into the kernel send
    // buffer, not bytes acknowledged, so it reads high early and decays. This is the honest
    // number to compare when A/B-ing the concurrency override.
    const transferredBytes = file.size - startingBytes;
    const elapsedSeconds = (Date.now() - transferStartedAt) / 1000;
    console.info(`[upload] ${(transferredBytes / 1048576).toFixed(0)} MB in ${elapsedSeconds.toFixed(1)}s `
        + `= ${(transferredBytes / 1048576 / elapsedSeconds).toFixed(2)} MB/s `
        + `(concurrency ${concurrency}, ${partCount} parts of ${(partSize / 1048576).toFixed(0)} MB, `
        + `${throttled} throttled)`);

    if (throttled) {
        console.warn(`[upload] Spaces returned 503 Slow Down ${throttled} time(s). `
            + `Lower the concurrency (localStorage crowd.uploadConcurrency) and compare.`);
    }

    // Step 3: assemble the parts into the final object. The backend pulls the part ETags from
    // Spaces itself; we just tell it how many parts there should be so it can verify completeness.
    report(file.size, file.size, "finalizing");
    const completeRes = await api("/files/complete", { key, uploadId, partCount, crowdJobId, fileInputId, storageTargetId: session.storageTargetId });
    if (!completeRes.ok) {
        // The job's size limit is enforced here against the real byte count, so this is a
        // message worth showing rather than a generic failure.
        if (completeRes.status === 413) {
            const { error } = await completeRes.json().catch(() => ({}));
            throw new Error(error || "This file is too large for this job.");
        }
        throw new Error("Failed to complete upload");
    }
    const result = await completeRes.json();
    return result.key;
}
